/**
 * useGeoJsonLayers — memoised GeoJSON FeatureCollections for MapLibre.
 *
 * Takes the raw per-layer pin arrays and shapes them into the
 * FeatureCollection objects consumed by react-map-gl <Source> nodes,
 * memoising on input identity to avoid re-uploading tiles to the GPU
 * every render.
 *
 * Consumers: App.tsx renders the returned sources/layers into the
 * MapLibre canvas.
 */
import { useMemo } from "react";
import type { FolkloreRecord } from "../types/index.ts";
import { isFolkloreDuplicate } from "../utils/folkloreDedup.ts";
import { timelineState, emergence } from "../utils/yearRange.ts";
import { TIMELINE_MIN_YEAR } from "../utils/deepLink.ts";

// ============================================================
// useGeoJsonLayers — derives all GeoJSON FeatureCollections
// from raw feature arrays + filter state.
// ============================================================

const CHURCH_TYPES = new Set(["church", "cathedral", "chapel", "stave_church", "church_ruin"]);

/**
 * The timeline's split. With a year set, a dated feature is before its build
 * span (a ghost, hollow), inside it (live, with `tf` 0..1 so the pin fades in
 * across the register's era rather than snapping on a year the register
 * never gave), after it (live, full), or past a hand-dated end of use (a
 * ghost, ash grey). Ghosts of every period-bearing layer share one
 * unclustered source so clusters keep counting what stood there. An undated
 * feature has no y0 and stays live and grey; the slider must not make the
 * map look emptier than the registers are. `siteDates` are the hand tables
 * (public/data/site-dates.json) keyed by feature id and win over the register.
 */
export interface SiteDate { y0: number; y1: number; y2?: number; source?: string }
export function splitByYear(features: GeoJSON.Feature[], year: number | null, siteDates?: Record<string, SiteDate>): { live: GeoJSON.Feature[]; ghost: GeoJSON.Feature[] } {
  if (year == null) return { live: features, ghost: [] };
  const live: GeoJSON.Feature[] = [], ghost: GeoJSON.Feature[] = [];
  for (const f of features) {
    const p = f.properties;
    const hand = siteDates?.[String(p?.id)];
    const y0 = hand?.y0 ?? p?.y0, y1 = hand?.y1 ?? p?.y1, y2 = hand?.y2;
    if (typeof y0 !== "number" || typeof y1 !== "number") { live.push(f); continue; }
    const st = timelineState(y0, y1, year, y2);
    if (st === "in") live.push(hand ? { ...f, properties: { ...p, y0, y1, ...(y2 != null ? { y2 } : {}), dated: hand.source ?? "" } } : f);
    else if (st === "emerging") live.push({ ...f, properties: { ...p, y0, y1, tf: emergence(Math.max(y0, TIMELINE_MIN_YEAR), y1, year), ...(hand ? { dated: hand.source ?? "" } : {}) } });
    else ghost.push({ ...f, properties: { ...p, y0, y1, ts: st, ...(hand ? { dated: hand.source ?? "" } : {}) } });
  }
  return { live, ghost };
}

/** Which period-bearing layers are switched on, so a hidden layer casts no ghosts. */
/**
 * How many ghosts the map will draw at once. They are one unclustered source
 * across twelve layers, so a viewport holding every layer at its 2 000-record
 * truncation can reach five figures, and unclustered points are the one thing
 * that costs a phone's GPU real memory. Past this the ghosts are dropped for
 * that year: the live pins still say what stood there, which is the feature,
 * and the reader loses the outlines rather than the map.
 */
export const MAX_GHOSTS = 8000;

export interface GhostShow { heritage: boolean; barrow: boolean; rockart: boolean; industrial: boolean; shipwreck: boolean; building: boolean; runestone: boolean; battlefield: boolean; megalith: boolean; stolperstein: boolean; lighthouse: boolean; monument: boolean }

export interface GeoJsonLayersInput {
  // Feature arrays
  heritageFeatures: GeoJSON.Feature[];
  folkloreFeatures: GeoJSON.Feature[];
  runestoneFeatures: GeoJSON.Feature[];
  battlefieldFeatures: GeoJSON.Feature[];
  monumentFeatures: GeoJSON.Feature[];
  buildingFeatures: GeoJSON.Feature[];
  shipwreckFeatures: GeoJSON.Feature[];
  museumFeatures: GeoJSON.Feature[];
  rockArtFeatures: GeoJSON.Feature[];
  unescoFeatures: GeoJSON.Feature[];
  industrialFeatures: GeoJSON.Feature[];
  barrowFeatures: GeoJSON.Feature[];
  megalithFeatures: GeoJSON.Feature[];
  townscapeFeatures: GeoJSON.Feature[];
  plaqueFeatures: GeoJSON.Feature[];
  stolpersteinFeatures: GeoJSON.Feature[];
  lighthouseFeatures: GeoJSON.Feature[];
  thingsiteFeatures: GeoJSON.Feature[];
  byleksikonFeatures: GeoJSON.Feature[];

  // Filter state
  activePeriod: string;
  /** The timeline's year, or null while the bar is closed. */
  timelineYear: number | null;
  ghostShow: GhostShow;
  /** Hand-dated sites, id → span; empty until the file has loaded. */
  siteDates: Record<string, SiteDate>;
  activeFolkloreCat: string;
  activeMonumentCat: string;
  showBuildingPoints: boolean;

  // Indexes (for folklore grouping)
  folkloreIndex: React.RefObject<Map<string, FolkloreRecord>>;

  // Visited / saved overlay
  visitedIdSet: Set<string>;
  savedIdSet: Set<string>;
}

export interface GeoJsonLayersOutput {
  heritageGeoJSON: GeoJSON.FeatureCollection;
  folkloreGeoJSON: GeoJSON.FeatureCollection;
  runestoneGeoJSON: GeoJSON.FeatureCollection;
  battlefieldGeoJSON: GeoJSON.FeatureCollection;
  monumentGeoJSON: GeoJSON.FeatureCollection;
  buildingGeoJSON: GeoJSON.FeatureCollection;
  shipwreckGeoJSON: GeoJSON.FeatureCollection;
  museumGeoJSON: GeoJSON.FeatureCollection;
  rockArtGeoJSON: GeoJSON.FeatureCollection;
  unescoGeoJSON: GeoJSON.FeatureCollection;
  industrialGeoJSON: GeoJSON.FeatureCollection;
  barrowGeoJSON: GeoJSON.FeatureCollection;
  megalithGeoJSON: GeoJSON.FeatureCollection;
  townscapeGeoJSON: GeoJSON.FeatureCollection;
  plaqueGeoJSON: GeoJSON.FeatureCollection;
  stolpersteinGeoJSON: GeoJSON.FeatureCollection;
  lighthouseGeoJSON: GeoJSON.FeatureCollection;
  thingsiteGeoJSON: GeoJSON.FeatureCollection;
  byleksikonGeoJSON: GeoJSON.FeatureCollection;
  visitedOverlayGeoJSON: GeoJSON.FeatureCollection;
  savedOverlayGeoJSON: GeoJSON.FeatureCollection;
  /** Not-yet-built and gone pins of every period-bearing layer, for the timeline. */
  ghostGeoJSON: GeoJSON.FeatureCollection;
}

export function useGeoJsonLayers(input: GeoJsonLayersInput): GeoJsonLayersOutput {
  const {
    heritageFeatures, folkloreFeatures, runestoneFeatures, battlefieldFeatures,
    monumentFeatures, buildingFeatures, shipwreckFeatures, museumFeatures,
    rockArtFeatures, unescoFeatures, industrialFeatures, barrowFeatures,
    megalithFeatures, townscapeFeatures, plaqueFeatures, stolpersteinFeatures,
    lighthouseFeatures, thingsiteFeatures, byleksikonFeatures,
    activePeriod, activeFolkloreCat, activeMonumentCat, showBuildingPoints,
    timelineYear, ghostShow, siteDates,
    folkloreIndex,
    visitedIdSet, savedIdSet,
  } = input;

  // --- Layer GeoJSON ---

  const heritageByPeriod = useMemo<GeoJSON.Feature[]>(() =>
    activePeriod === "all" ? heritageFeatures
      : activePeriod === "Sami" ? heritageFeatures.filter((f) => f.properties?.sami === true).map(f => ({ ...f, properties: { ...f.properties, period: "Sami" } }))
      : heritageFeatures.filter((f) => f.properties?.period === activePeriod),
  [heritageFeatures, activePeriod]);
  const heritageSplit = useMemo(() => splitByYear(heritageByPeriod, timelineYear, siteDates), [heritageByPeriod, timelineYear, siteDates]);
  const heritageGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: heritageSplit.live,
  }), [heritageSplit]);

  const folkloreGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => {
    // Group raw features by coordinate into one feature per location
    const byCoord = new Map<string, { lng: number; lat: number; records: FolkloreRecord[] }>();
    const seenIds = new Set<string>();
    for (const f of folkloreFeatures) {
      const id = f.properties?.id as string;
      if (seenIds.has(id)) continue; // skip duplicate feature entries
      seenIds.add(id);
      const rec = folkloreIndex.current?.get(id);
      if (!rec) continue;
      if (activeFolkloreCat !== "all" && rec.category !== activeFolkloreCat) continue;
      const ck = `${rec.lat},${rec.lng}`;
      const g = byCoord.get(ck);
      if (g) {
        if (!isFolkloreDuplicate(g.records, rec)) g.records.push(rec);
      } else { byCoord.set(ck, { lng: rec.lng, lat: rec.lat, records: [rec] }); }
    }
    const features: GeoJSON.Feature[] = Array.from(byCoord.entries()).map(([ck, g]) => ({
      type: "Feature" as const,
      geometry: { type: "Point" as const, coordinates: [g.lng, g.lat] },
      properties: { coordKey: ck, storyCount: g.records.length, category: g.records[0].category, country: g.records[0].country, layerType: "folklore" },
    }));
    return { type: "FeatureCollection", features };
  }, [folkloreFeatures, activeFolkloreCat]); // eslint-disable-line react-hooks/exhaustive-deps

  const runestoneSplit = useMemo(() => splitByYear(runestoneFeatures, timelineYear, siteDates), [runestoneFeatures, timelineYear, siteDates]);
  const runestoneGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: runestoneSplit.live,
  }), [runestoneSplit]);

  const battlefieldSplit = useMemo(() => splitByYear(battlefieldFeatures, timelineYear, siteDates), [battlefieldFeatures, timelineYear, siteDates]);
  const battlefieldGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: battlefieldSplit.live,
  }), [battlefieldSplit]);

  const monumentByCat = useMemo<GeoJSON.Feature[]>(() => {
    if (activeMonumentCat === "protected") return []; // buildings shown in their own source
    return activeMonumentCat === "all"
      ? monumentFeatures
      : activeMonumentCat === "church"
        ? monumentFeatures.filter(f => CHURCH_TYPES.has(f.properties?.monumentType))
        : monumentFeatures.filter(f => f.properties?.monumentType === activeMonumentCat);
  }, [monumentFeatures, activeMonumentCat]);
  const monumentSplit = useMemo(() => splitByYear(monumentByCat, timelineYear, siteDates), [monumentByCat, timelineYear, siteDates]);
  const monumentGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: monumentSplit.live,
  }), [monumentSplit]);

  const buildingSplit = useMemo(() => splitByYear(showBuildingPoints ? buildingFeatures : [], timelineYear, siteDates), [buildingFeatures, showBuildingPoints, timelineYear, siteDates]);
  const buildingGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: buildingSplit.live,
  }), [buildingSplit]);

  const shipwreckSplit = useMemo(() => splitByYear(shipwreckFeatures, timelineYear, siteDates), [shipwreckFeatures, timelineYear, siteDates]);
  const shipwreckGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: shipwreckSplit.live,
  }), [shipwreckSplit]);

  const museumGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: museumFeatures,
  }), [museumFeatures]);

  const rockArtSplit = useMemo(() => splitByYear(rockArtFeatures, timelineYear, siteDates), [rockArtFeatures, timelineYear, siteDates]);
  const rockArtGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: rockArtSplit.live,
  }), [rockArtSplit]);

  const unescoGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: unescoFeatures,
  }), [unescoFeatures]);

  const industrialSplit = useMemo(() => splitByYear(industrialFeatures, timelineYear, siteDates), [industrialFeatures, timelineYear, siteDates]);
  const industrialGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: industrialSplit.live,
  }), [industrialSplit]);

  const barrowSplit = useMemo(() => splitByYear(barrowFeatures, timelineYear, siteDates), [barrowFeatures, timelineYear, siteDates]);
  const barrowGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: barrowSplit.live,
  }), [barrowSplit]);

  const megalithSplit = useMemo(() => splitByYear(megalithFeatures, timelineYear, siteDates), [megalithFeatures, timelineYear, siteDates]);
  const megalithGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: megalithSplit.live,
  }), [megalithSplit]);

  const townscapeGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: townscapeFeatures,
  }), [townscapeFeatures]);

  const plaqueGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: plaqueFeatures,
  }), [plaqueFeatures]);

  const stolpersteinSplit = useMemo(() => splitByYear(stolpersteinFeatures, timelineYear, siteDates), [stolpersteinFeatures, timelineYear, siteDates]);
  const stolpersteinGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: stolpersteinSplit.live,
  }), [stolpersteinSplit]);

  const lighthouseSplit = useMemo(() => splitByYear(lighthouseFeatures, timelineYear, siteDates), [lighthouseFeatures, timelineYear, siteDates]);
  const lighthouseGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: lighthouseSplit.live,
  }), [lighthouseSplit]);

  const thingsiteGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: thingsiteFeatures,
  }), [thingsiteFeatures]);

  const byleksikonGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: "FeatureCollection", features: byleksikonFeatures,
  }), [byleksikonFeatures]);

  // --- Visited / Saved overlays ---

  const visitedOverlayGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => {
    if (visitedIdSet.size === 0) return { type: "FeatureCollection", features: [] };
    const features: GeoJSON.Feature[] = [];
    const addFromFeatures = (feats: GeoJSON.Feature[]) => {
      for (const f of feats) {
        if (f.properties?.id && visitedIdSet.has(f.properties.id) && f.geometry.type === "Point") {
          features.push(f);
        }
      }
    };
    addFromFeatures(heritageFeatures);
    addFromFeatures(runestoneFeatures);
    addFromFeatures(battlefieldFeatures);
    addFromFeatures(monumentFeatures);
    addFromFeatures(buildingFeatures);
    addFromFeatures(shipwreckFeatures);
    addFromFeatures(museumFeatures);
    addFromFeatures(rockArtFeatures);
    addFromFeatures(unescoFeatures);
    addFromFeatures(industrialFeatures);
    addFromFeatures(barrowFeatures);
    addFromFeatures(megalithFeatures);
    addFromFeatures(townscapeFeatures);
    addFromFeatures(plaqueFeatures);
    addFromFeatures(stolpersteinFeatures);
    addFromFeatures(lighthouseFeatures);
    addFromFeatures(thingsiteFeatures);
    addFromFeatures(byleksikonFeatures);
    return { type: "FeatureCollection", features };
  }, [visitedIdSet, heritageFeatures, runestoneFeatures, battlefieldFeatures, monumentFeatures, buildingFeatures, shipwreckFeatures, museumFeatures, rockArtFeatures, unescoFeatures, industrialFeatures, barrowFeatures, megalithFeatures, townscapeFeatures, plaqueFeatures, stolpersteinFeatures, lighthouseFeatures, thingsiteFeatures, byleksikonFeatures]);

  const savedOverlayGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => {
    if (savedIdSet.size === 0) return { type: "FeatureCollection", features: [] };
    const features: GeoJSON.Feature[] = [];
    const addFromFeatures = (feats: GeoJSON.Feature[]) => {
      for (const f of feats) {
        if (f.properties?.id && savedIdSet.has(f.properties.id) && f.geometry.type === "Point") {
          features.push(f);
        }
      }
    };
    addFromFeatures(heritageFeatures);
    addFromFeatures(runestoneFeatures);
    addFromFeatures(battlefieldFeatures);
    addFromFeatures(monumentFeatures);
    addFromFeatures(buildingFeatures);
    addFromFeatures(shipwreckFeatures);
    addFromFeatures(museumFeatures);
    addFromFeatures(rockArtFeatures);
    addFromFeatures(unescoFeatures);
    addFromFeatures(industrialFeatures);
    addFromFeatures(barrowFeatures);
    addFromFeatures(megalithFeatures);
    addFromFeatures(townscapeFeatures);
    addFromFeatures(plaqueFeatures);
    addFromFeatures(stolpersteinFeatures);
    addFromFeatures(lighthouseFeatures);
    addFromFeatures(thingsiteFeatures);
    addFromFeatures(byleksikonFeatures);
    return { type: "FeatureCollection", features };
  }, [savedIdSet, heritageFeatures, runestoneFeatures, battlefieldFeatures, monumentFeatures, buildingFeatures, shipwreckFeatures, museumFeatures, rockArtFeatures, unescoFeatures, industrialFeatures, barrowFeatures, megalithFeatures, townscapeFeatures, plaqueFeatures, stolpersteinFeatures, lighthouseFeatures, thingsiteFeatures, byleksikonFeatures]);

  const ghostGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => {
    const features = timelineYear == null ? [] : [
      ...(ghostShow.heritage ? heritageSplit.ghost : []),
      ...(ghostShow.barrow ? barrowSplit.ghost : []),
      ...(ghostShow.rockart ? rockArtSplit.ghost : []),
      ...(ghostShow.industrial ? industrialSplit.ghost : []),
      ...(ghostShow.shipwreck ? shipwreckSplit.ghost : []),
      ...(ghostShow.building ? buildingSplit.ghost : []),
      ...(ghostShow.runestone ? runestoneSplit.ghost : []),
      ...(ghostShow.battlefield ? battlefieldSplit.ghost : []),
      ...(ghostShow.megalith ? megalithSplit.ghost : []),
      ...(ghostShow.stolperstein ? stolpersteinSplit.ghost : []),
      ...(ghostShow.lighthouse ? lighthouseSplit.ghost : []),
      ...(ghostShow.monument ? monumentSplit.ghost : []),
    ];
    return { type: "FeatureCollection", features: features.length > MAX_GHOSTS ? [] : features };
  }, [timelineYear, ghostShow, heritageSplit, barrowSplit, rockArtSplit, industrialSplit, shipwreckSplit, buildingSplit, runestoneSplit, battlefieldSplit, megalithSplit, stolpersteinSplit, lighthouseSplit, monumentSplit]);

  return {
    ghostGeoJSON,
    heritageGeoJSON, folkloreGeoJSON, runestoneGeoJSON, battlefieldGeoJSON,
    monumentGeoJSON, buildingGeoJSON, shipwreckGeoJSON, museumGeoJSON,
    rockArtGeoJSON, unescoGeoJSON, industrialGeoJSON, barrowGeoJSON,
    megalithGeoJSON, townscapeGeoJSON, plaqueGeoJSON, stolpersteinGeoJSON,
    lighthouseGeoJSON, thingsiteGeoJSON, byleksikonGeoJSON,
    visitedOverlayGeoJSON, savedOverlayGeoJSON,
  };
}
